import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Crown } from 'lucide-react';
import { useSpaceStore } from '../../stores/spaceStore';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const emojis = ['👤', '👋', '😊', '🤠', '👻', '🤖', '🎃', '🦄', '🐶', '🐱', '🐼', '🦁'];

interface MemberSlot {
  id: string;
  displayName: string;
  avatarEmoji: string;
}

const FREE_LIMIT = 2;
const PRO_LIMIT = 6;

const getMemberLimit = (spaceType: string, isPro: boolean): number => {
  if (spaceType === 'couple') return 2;
  return isPro ? PRO_LIMIT : FREE_LIMIT;
};

const getMemberLabel = (spaceType: string, memberIndex: number): string => {
  const labels: Record<string, string[]> = {
    couple: ["Partner's Name"],
    family: ["Family Member 1", "Family Member 2", "Family Member 3", "Family Member 4", "Family Member 5"],
    roommates: ["Roommate 1", "Roommate 2", "Roommate 3", "Roommate 4", "Roommate 5"],
    trip: ["Traveler 1", "Traveler 2", "Traveler 3", "Traveler 4", "Traveler 5"],
  };
  const typeLabels = labels[spaceType] || labels.couple;
  return typeLabels[memberIndex] || `Member ${memberIndex + 2}`;
};

const SUBTITLES: Record<string, string> = {
  couple: "Set up your profile and your partner's",
  family: "Set up your profile and add family members",
  roommates: "Set up your profile and add roommates",
  trip: "Set up your profile and add travelers",
};

const PLACEHOLDER_NAMES = ['e.g., Sam', 'Member 2', 'Member 3', 'Member 4', 'Member 5', 'Member 6'];

export default function OnboardingMembers() {
  const navigate = useNavigate();
  const { currentSpace, fetchMembers, saveOnboardingMembers } = useSpaceStore();
  const { user, isPro } = useAuthStore();
  
  const spaceType = currentSpace?.space_type || 'couple';
  const isCouple = spaceType === 'couple';
  const memberLimit = getMemberLimit(spaceType, isPro);

  const [isLoading, setIsLoading] = useState(false);
  const [ownerData, setOwnerData] = useState({
    displayName: (user as any)?.user_metadata?.display_name || '',
    avatarEmoji: emojis[0]
  });

  const [extraSlots, setExtraSlots] = useState<MemberSlot[]>([
    { id: 'slot-1', displayName: '', avatarEmoji: emojis[1] }
  ]);
  const [duplicateError, setDuplicateError] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!currentSpace) {
      navigate('/onboarding/space');
    }
  }, [currentSpace, navigate]);

  const validate = useCallback((): { valid: boolean; error: string | null } => {
    setDuplicateError({});
    const errors: Record<string, string> = {};

    if (!ownerData.displayName.trim()) {
      return { valid: false, error: 'Please enter your display name' };
    }

    const allNames = [ownerData.displayName.trim().toLowerCase()];
    const filledSlots = extraSlots.filter(s => s.displayName.trim());
    
    if (filledSlots.length < 1) {
      return { valid: false, error: 'You need at least 1 other member' };
    }
    
    for (const slot of extraSlots) {
      const name = slot.displayName.trim();
      if (!name) {
        return { valid: false, error: 'All member names are required' };
      }
      if (name.length > 50) {
        return { valid: false, error: 'Names must be 50 characters or less' };
      }
      if (allNames.includes(name.toLowerCase())) {
        errors[slot.id] = 'Duplicate name';
      }
      allNames.push(name.toLowerCase());
    }

    setDuplicateError(errors);
    if (Object.keys(errors).length > 0) {
      return { valid: false, error: 'Please fix duplicate names' };
    }
    return { valid: true, error: null };
  }, [ownerData, extraSlots]);

  const addSlot = () => {
    if (1 + extraSlots.length >= memberLimit) {
      toast('🔒 Upgrade to Pro to add up to 6 members', { icon: '👑' });
      return;
    }
    const id = `slot-${Date.now()}`;
    setExtraSlots(prev => [
      ...prev,
      { id, displayName: '', avatarEmoji: emojis[(prev.length + 1) % emojis.length] }
    ]);
  };

  const removeSlot = (id: string) => {
    if (extraSlots.length <= 1 && !isCouple) return;
    setExtraSlots(prev => prev.filter(s => s.id !== id));
  };

  const updateSlot = (id: string, field: 'displayName' | 'avatarEmoji', value: string) => {
    setExtraSlots(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { valid, error } = validate();
    if (!valid) {
      toast.error(error || 'Validation failed');
      return;
    }

    setIsLoading(true);
    try {
      await saveOnboardingMembers(
        currentSpace!.id,
        {
          display_name: ownerData.displayName.trim(),
          avatar_emoji: ownerData.avatarEmoji,
        },
        extraSlots.map((slot) => ({
          display_name: slot.displayName.trim(),
          avatar_emoji: slot.avatarEmoji,
        }))
      );

      await fetchMembers(currentSpace!.id);
      navigate('/onboarding/categories');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save members');
    } finally {
      setIsLoading(false);
    }
  };

  const EmojiPicker = ({ selected, onSelect }: { selected: string; onSelect: (e: string) => void }) => (
    <div className="grid grid-cols-6 gap-2 px-2">
      {emojis.map(emoji => (
        <button
          key={emoji}
          type="button"
          onClick={() => onSelect(emoji)}
          className={`flex items-center justify-center w-full aspect-square rounded-xl text-2xl transition-colors touch-target ${
            selected === emoji
              ? 'bg-primary/20 ring-2 ring-primary'
              : 'hover:bg-muted/10'
          }`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );

  const sectionLabel = isCouple ? "Partner" : (getMemberLabel(spaceType, 0) || "Members");
  const subtitle = SUBTITLES[spaceType] || SUBTITLES.couple;
  const filledExtraSlots = extraSlots.filter((slot) => slot.displayName.trim()).length;
  const enteredMemberCount = (ownerData.displayName.trim() ? 1 : 0) + filledExtraSlots;
  const memberCountLabel = enteredMemberCount === 1 ? '1 member' : `${enteredMemberCount} members`;

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 flex flex-col max-w-md mx-auto w-full"
      >
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Add Members</h1>
          <p className="text-muted">{subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col space-y-6">
          {/* Owner */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <h3 className="font-semibold mb-4">Your Profile</h3>
            <div className="space-y-4">
              <EmojiPicker
                selected={ownerData.avatarEmoji}
                onSelect={(e) => setOwnerData({ ...ownerData, avatarEmoji: e })}
              />
              <Input
                label="Your Display Name"
                placeholder="e.g., Alex"
                value={ownerData.displayName}
                onChange={(e) => setOwnerData({ ...ownerData, displayName: e.target.value })}
                maxLength={50}
                required
              />
            </div>
          </div>

          {/* Extra Members */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{sectionLabel}</h3>
              <span className="text-xs text-muted">
                {enteredMemberCount} / {memberLimit}
              </span>
            </div>
            
            <div className="space-y-4">
              <AnimatePresence>
                {extraSlots.map((slot, index) => (
                  <motion.div
                    key={slot.id}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative"
                  >
                    <div className="space-y-3 pt-3 border-t border-border/50">
                      <EmojiPicker
                        selected={slot.avatarEmoji}
                        onSelect={(e) => updateSlot(slot.id, 'avatarEmoji', e)}
                      />
                      <div className="relative">
                        <Input
                          label={index === 0 ? "Name" : `Member ${index + 2}`}
                          placeholder={PLACEHOLDER_NAMES[index] || `Member ${index + 2}`}
                          value={slot.displayName}
                          onChange={(e) => updateSlot(slot.id, 'displayName', e.target.value)}
                          maxLength={50}
                          error={duplicateError[slot.id]}
                        />
                        {!isCouple && extraSlots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSlot(slot.id)}
                            className="absolute right-2 top-8 p-1 rounded-full text-muted hover:text-danger hover:bg-danger/10 transition-colors touch-target"
                            aria-label="Remove member"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {!isCouple && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <button
                  type="button"
                  onClick={addSlot}
                  disabled={1 + extraSlots.length >= memberLimit}
                  className={`w-full py-3 px-4 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 touch-target ${
                    1 + extraSlots.length >= memberLimit
                      ? 'bg-muted/10 text-muted cursor-not-allowed'
                      : 'border-2 border-dashed border-muted/40 text-muted hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {1 + extraSlots.length >= memberLimit && !isPro ? (
                    <>
                      <Crown size={16} />
                      Upgrade to Pro for up to 6 members
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Add another member
                    </>
                  )}
                </button>
              </div>
            )}

            <p className="text-sm text-muted mt-3">
              {isCouple
                ? "Your partner can join via invite link after setup"
                : "Members can be invited via link after setup"
              }
            </p>
          </div>

          <div className="flex-1" />

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Saving...' : `Continue (${memberCountLabel})`}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
